import { getAuthHeader } from "./authHeader";
// frontend/src/services/campaignApi.js
import axios from "axios";
import {
  apiCall,
  handleApiError as enhancedApiError,
} from "../utils/apiErrorHandler";
import { withTimeout, getTimeoutForOperation, TIMEOUT_CONFIG } from "./api";
import { createRequestManager } from "./timeoutWrapper";
import { API_BASE_URL } from "../apiConstants";
import { toSnake } from "../utils/caseConverter";

const API_URL = `${API_BASE_URL}/campaigns`;

// Request manager for cancelling requests
const requestManager = createRequestManager();

// Helper function to handle errors consistently
const handleApiError = (error, context = "operation") => {
  return enhancedApiError(error, `campaign ${context}`);
};

const normalizeListParams = (params) => {
  const { limit, perPage, ...query } = params;
  const requestedPageSize = perPage ?? limit;

  return requestedPageSize === undefined
    ? query
    : { ...query, per_page: requestedPageSize };
};

const campaignApi = {
  getCreationDefaults: async () => {
    const response = await axios.get(`${API_URL}/creation/defaults`, getAuthHeader());
    const defaults = toSnake(response.data?.data);
    if (!defaults || typeof defaults !== "object" || !Array.isArray(defaults.days_of_week)) {
      throw new Error("Campaign creation defaults are unavailable.");
    }
    return defaults;
  },

  /**
   * Fetches a list of campaigns with pagination, filtering, and sorting.
   * @param {object} params - Query parameters (e.g., page, limit, search, status, sortBy, sortOrder)
   * @returns {Promise<object>} - Promise resolving to { campaigns: [], page, pages, total }
   */
  getAll: async (params = {}) => {
    return apiCall(
      async () => {
        const authConfig = getAuthHeader();
        const config = withTimeout(
          {
            params: normalizeListParams(params),
            ...authConfig,
          },
          getTimeoutForOperation("quick"),
        ); // 10s for list queries

        // Track request for potential cancellation
        requestManager.add("campaigns.getAll", config.controller);

        try {
          const response = await axios.get(API_URL, config);
          requestManager.remove("campaigns.getAll");
          const responseData = response.data;
          const campaigns = responseData.campaigns ?? responseData.data;

          if (
            !Array.isArray(campaigns) ||
            !Number.isInteger(responseData.page) ||
            !Number.isInteger(responseData.pages) ||
            !Number.isInteger(responseData.total)
          ) {
            throw new Error(
              "Campaign list response is missing valid pagination metadata.",
            );
          }

          return {
            data: campaigns,
            campaigns,
            page: responseData.page,
            pages: responseData.pages,
            total: responseData.total,
          };
        } catch (error) {
          requestManager.remove("campaigns.getAll");
          throw error;
        }
      },
      {
        context: { operation: "campaign fetch" },
        retries: 3,
      },
    );
  },

  /**
   * Fetches a single campaign by its ID.
   * @param {string|number} id - The ID of the campaign.
   * @returns {Promise<object>} - Promise resolving to the campaign object.
   */
  getById: async (id) => {
    return apiCall(
      async () => {
        const authConfig = getAuthHeader();
        const config = withTimeout(authConfig, getTimeoutForOperation("quick"));

        requestManager.add(`campaigns.getById.${id}`, config.controller);

        try {
          const response = await axios.get(`${API_URL}/${id}`, config);
          requestManager.remove(`campaigns.getById.${id}`);
          return response.data;
        } catch (error) {
          requestManager.remove(`campaigns.getById.${id}`);
          throw error;
        }
      },
      {
        context: { operation: `campaign fetch (ID: ${id})` },
        retries: 3,
      },
    );
  },

  /**
   * Creates a new campaign.
   * @param {object} campaignData - The data for the new campaign.
   * @returns {Promise<object>} - Promise resolving to the newly created campaign object.
   */
  create: async (campaignData) => {
    return apiCall(
      async () => {
        const authConfig = getAuthHeader();
        const config = withTimeout(
          authConfig,
          getTimeoutForOperation("normal"),
        );

        // Backend expects data wrapped in "campaign" key
        const response = await axios.post(
          API_URL,
          { campaign: campaignData },
          config,
        );
        return response.data;
      },
      {
        context: { operation: "campaign creation" },
        retries: 2,
      },
    );
  },

  /**
   * Updates an existing campaign.
   * @param {string|number} id - The ID of the campaign to update.
   * @param {object} campaignData - The updated data for the campaign.
   * @returns {Promise<object>} - Promise resolving to the updated campaign object.
   */
  update: async (id, campaignData) => {
    try {
      const authConfig = getAuthHeader();
      // Backend expects data wrapped in "campaign" key
      const response = await axios.put(
        `${API_URL}/${id}`,
        { campaign: campaignData },
        authConfig,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, `update (ID: ${id})`);
    }
  },

  /**
   * Deletes a campaign.
   * @param {string|number} id - The ID of the campaign to delete.
   * @returns {Promise<object>} - Promise resolving to an empty object or confirmation message.
   */
  delete: async (id) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.delete(`${API_URL}/${id}`, authConfig);
      return response.data; // Often returns empty body or a success message
    } catch (error) {
      const errorInfo = handleApiError(error, {
        context: `delete (ID: ${id})`,
        showToast: false,
      });
      // Re-throw the error so the caller can handle it
      throw error;
    }
  },

  start: async (id) => {
    return apiCall(
      async () => {
        const authConfig = getAuthHeader();
        const response = await axios.post(
          `${API_URL}/${id}/start`,
          {},
          authConfig,
        );
        return response.data;
      },
      {
        context: { operation: `campaign start (ID: ${id})` },
        retries: 0,
      },
    );
  },

  pause: async (id) => {
    return apiCall(
      async () => {
        const authConfig = getAuthHeader();
        const response = await axios.put(
          `${API_URL}/${id}/pause`,
          {},
          authConfig,
        );
        return response.data;
      },
      {
        context: { operation: `campaign pause (ID: ${id})` },
        retries: 0,
      },
    );
  },

  /**
   * Gets the next contact for a campaign (for dialer functionality)
   * @param {string|number} id - The ID of the campaign
   * @returns {Promise<object>} - Promise resolving to the next contact object
   */
  getNextContact: async (id) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.get(
        `${API_URL}/${id}/next-contact`,
        authConfig,
      );
      return response.data;
    } catch (error) {
      // The queue is closed while the campaign is not running: the backend
      // answers 400 {"error":"campaign_not_active"}. That is a state, not a
      // failure, so it is thrown with a code (and no toast) for callers to
      // render. Every other failure is still reported and rethrown; before,
      // it resolved to undefined and read as an empty queue.
      const code = error.response?.data?.error;
      if (error.response?.status === 400 && code === "campaign_not_active") {
        const closed = new Error(
          "This campaign is not active. Its queue opens when a manager starts it.",
        );
        closed.code = code;
        closed.status = 400;
        throw closed;
      }
      const info = await handleApiError(
        error,
        `get next contact (Campaign ID: ${id})`,
      );
      const failure = new Error(info?.message || error.message);
      failure.status = error.response?.status;
      failure.code = code;
      throw failure;
    }
  },

  getCampaignContact: async (id, contactId) => {
    try {
      const authConfig = getAuthHeader();
      const response = await axios.get(
        `${API_URL}/${id}/contacts/${contactId}`,
        authConfig,
      );
      return response.data;
    } catch (error) {
      await handleApiError(
        error,
        `get campaign contact (Campaign ID: ${id}, Contact ID: ${contactId})`,
      );
      throw error;
    }
  },

  /**
   * Gets analytics data for a campaign
   * @param {string|number} id - The ID of the campaign
   * @returns {Promise<object>} - Promise resolving to campaign analytics
   */
  getAnalytics: async (id) => {
    try {
      const authConfig = getAuthHeader();
      const config = withTimeout(authConfig, getTimeoutForOperation("normal")); // Analytics might take longer

      const response = await axios.get(`${API_URL}/${id}/analytics`, config);
      return response.data;
    } catch (error) {
      handleApiError(error, `get analytics (Campaign ID: ${id})`);
    }
  },

  /**
   * Bulk import contacts to a campaign
   * @param {string|number} id - The ID of the campaign
   * @param {File} file - CSV file with contacts
   * @returns {Promise<object>} - Promise resolving to import result
   */
  importContacts: async (id, file) => {
    return apiCall(
      async () => {
        const authConfig = getAuthHeader();
        const formData = new FormData();
        formData.append("file", file);

        const config = withTimeout(
          {
            ...authConfig,
            headers: {
              ...authConfig.headers,
              "Content-Type": "multipart/form-data",
            },
          },
          getTimeoutForOperation("upload"),
        ); // 2 minutes for file upload

        const response = await axios.post(
          `${API_URL}/${id}/contacts`,
          formData,
          config,
        );
        return response.data;
      },
      {
        context: { operation: `import contacts to campaign (ID: ${id})` },
        retries: 1,
      }, // Fewer retries for uploads
    );
  },

  /**
   * Remove a contact from a campaign
   */
  removeContactFromCampaign: async (campaignId, contactId) => {
    const response = await axios.delete(
      `${API_URL}/${campaignId}/contacts/${contactId}`,
    );
    return response.data;
  },

  /**
   * Add contacts to a campaign
   */
  addContacts: async (campaignId, contactIds) => {
    return apiCall(
      async () => {
        const authConfig = getAuthHeader();
        const config = withTimeout(
          authConfig,
          getTimeoutForOperation("normal"),
        );
        const response = await axios.post(
          `${API_URL}/${campaignId}/contacts`,
          { contact_ids: contactIds },
          config,
        );
        return response.data;
      },
      {
        context: { operation: `add contacts to campaign (ID: ${campaignId})` },
        retries: 1,
      },
    );
  },

  getEligibleAgents: async (id) => {
    return apiCall(
      async () => {
        const config = withTimeout(
          getAuthHeader(),
          getTimeoutForOperation("quick"),
        );
        const response = await axios.get(
          `${API_URL}/${id}/eligible-agents`,
          config,
        );
        return response.data;
      },
      {
        context: { operation: `eligible agent fetch (Campaign ID: ${id})` },
        retries: 2,
      },
    );
  },

  assignAgent: async (campaignId, userId) => {
    return apiCall(
      async () => {
        const config = withTimeout(
          getAuthHeader(),
          getTimeoutForOperation("normal"),
        );
        const response = await axios.post(
          `${API_URL}/${campaignId}/agents/${userId}`,
          {},
          config,
        );
        return response.data;
      },
      {
        context: {
          operation: `agent assignment (Campaign ID: ${campaignId})`,
        },
        retries: 0,
      },
    );
  },

  removeAgent: async (campaignId, userId) => {
    return apiCall(
      async () => {
        const config = withTimeout(
          getAuthHeader(),
          getTimeoutForOperation("normal"),
        );
        const response = await axios.delete(
          `${API_URL}/${campaignId}/agents/${userId}`,
          config,
        );
        return response.data;
      },
      {
        context: {
          operation: `agent removal (Campaign ID: ${campaignId})`,
        },
        retries: 0,
      },
    );
  },

  activateMembership: async (campaignId) => {
    return apiCall(
      async () => {
        const config = withTimeout(
          getAuthHeader(),
          getTimeoutForOperation("normal"),
        );
        const response = await axios.post(
          `${API_URL}/${campaignId}/activation`,
          {},
          config,
        );
        return response.data;
      },
      {
        context: {
          operation: `campaign join (Campaign ID: ${campaignId})`,
        },
        retries: 0,
      },
    );
  },

  deactivateMembership: async (campaignId) => {
    return apiCall(
      async () => {
        const config = withTimeout(
          getAuthHeader(),
          getTimeoutForOperation("normal"),
        );
        const response = await axios.delete(
          `${API_URL}/${campaignId}/activation`,
          config,
        );
        return response.data;
      },
      {
        context: {
          operation: `campaign leave (Campaign ID: ${campaignId})`,
        },
        retries: 0,
      },
    );
  },

  skipContact: async (campaignId, contactId) => {
    return apiCall(
      async () => {
        const config = withTimeout(
          getAuthHeader(),
          getTimeoutForOperation("normal"),
        );
        const response = await axios.post(
          `${API_URL}/${campaignId}/skip-contact`,
          { contactId },
          config,
        );
        return response.data;
      },
      {
        context: {
          operation: `campaign contact skip (Campaign ID: ${campaignId})`,
        },
        retries: 0,
      },
    );
  },

  /**
   * Cancel all pending campaign requests
   */
  cancelAllRequests: () => {
    requestManager.cancelAll();
  },
};

export default campaignApi;
